import React, { useState, useEffect } from "react";
import {
  Loader2,
  Link,
  UserCheck,
  Globe,
  MapPin,
  Map,
  Hash,
  User,
  Phone, 
  Mail,
  Lock,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { GetLeadDetailsByUID } from "../Services/RecruitmentService";
import NotificationAlert from "../../../../CustomAlerts/NotificationAlert";
import { UpdateBasicDetails } from "../Services/LeadService";
import { FetchPinCodeDetails } from "../../../../../services/CommonServices";
import { GetSourceMetaData } from "../Services/LeadService";

const InputField = ({
  label,
  required,
  placeHolder,
  icon: Icon,
  value,
  onChange,
  error,
  readOnly,
  maxLength,
}) => {
  return (
    <div className="space-y-1">
      <label className="text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div
        className={`flex items-center border rounded-xl px-3 py-2 shadow-sm transition
                             ${
                               readOnly
                                 ? "bg-gray-100 border-gray-200"
                                 : "bg-white border-gray-200 focus-within:ring-2 focus-within:ring-blue-500"
                             }`}
      >
        {Icon && <Icon className="text-gray-400 mr-2" size={18} />}
        <input
          value={value}
          onChange={onChange}
          type="text"
          placeholder={placeHolder}
          readOnly={readOnly}
          maxLength={maxLength}
          className={`w-full bg-transparent outline-none text-sm
                                 ${
                                   readOnly
                                     ? "cursor-not-allowed text-gray-500 select-none"
                                     : "text-gray-800"
                                 }`}
        />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
};

const EditBasicDetails = () => {
  const { leadUID } = useParams();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({});
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFetchingPinCode, setIsFetchingPinCode] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  //   const skipPinCodeEffect = useRef();
  const [sourceOptions, setSourceOptions] = useState([]);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [filteredSources, setFilteredSources] = useState([]);
  const [activeSourceIndex, setActiveSourceIndex] = useState(-1);

  useEffect(() => {
    fetchSourceMetaData();
  }, []);

  const fetchSourceMetaData = async () => {
    try {
      const res = await GetSourceMetaData();
      if (res?.status === "Success") {
        setSourceOptions(res?.data || []);
        setFilteredSources(res?.data || []);
      }
    } catch (error) {
      setSourceOptions([]);
      setFilteredSources([]);
    }
  };

  useEffect(() => {
    if (form.pinCode?.length !== 6) return;
    // if(skipPinCodeEffect.current){
    //     skipPinCodeEffect.current = false;
    //     return;
    // }

    if (form.pinCode === "999999") {
      setCityOptions([]);
      setFilteredCities([]);

      setForm((prev) => ({
        ...prev,
        city: "Other",
        state: "Other",
        country: "India",
      }));
      setErrors((prev) => ({
        ...prev,
        pinCode: "",
      }));
      return;
    }
    fetchPinCodeDetails(form.pinCode);
  }, [form.pinCode]);

  const fetchPinCodeDetails = async (pinCode) => {
    if (pinCode.length !== 6) return;
    try {
      // setIsFetchingPinCode(true);
      const res = await FetchPinCodeDetails(pinCode);
      if (res?.status === "success") {
        const cityList =
          res?.city
            ?.split(",")
            ?.map((item) => item.trim())
            ?.filter(Boolean) || [];
        setCityOptions(cityList);
        setFilteredCities(cityList);

        setForm((prev) => ({
          ...prev,
          // city: cityList[0] || "",
          state: res?.state || "",
          country: res?.country || "",
        }));

        setErrors((prev) => ({
          ...prev,
          pinCode: "",
        }));
      }
    } catch (error) {
      setCityOptions([]);
      setFilteredCities([]);
      const errorMessagge =
        error?.message === "No data found for this pincode."
          ? "No location found for this pincode. Please enter manually."
          : "Pincode service unavailable. Please enter location details manually.";

      setErrors((prev) => ({
        ...prev,
        pinCode: errorMessagge,
      }));
    } finally {
      setIsFetchingPinCode(false);
    }
  };

  useEffect(() => {
    if (leadUID) {
      fetchLead(leadUID);
    }
  }, [leadUID]);

  const fetchLead = async (leadUID) => {
    try {
      const res = await GetLeadDetailsByUID(leadUID);
      const basic = res?.leadDetails?.[0] || {};
      //   skipPinCodeEffect.current = true;
      setForm({
        leadUID: basic.leadUID || "",
        name: basic.name || "",
        email: basic.email || "",
        phoneNo: basic.phoneNo || "",
        gender: basic.gender || "",
        pinCode: basic.pinCode || "",
        city: basic.city || "",
        state: basic.state || "",
        country: basic.country || "",
        source: basic.source || "",
      });
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Server error. Please try again later.",
      });
    }
  };

  const handleChange = (key) => (e) => {
    let value = e.target.value;
    if (key === "pinCode") {
      value = value.replace(/\D/g, "");
    }
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    const isEmpty = (val) => !val || val.trim() === "";

    if (isEmpty(form.name)) {
      errors.name = "Please enter name.";
    }
    if (isEmpty(form.email)) {
      errors.email = "Please enter email.";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      errors.email = "Please enter valid email.";
    }
    if (isEmpty(form.gender)) {
      errors.gender = "Please select gender.";
    }
    if (isEmpty(form.pinCode)) {
      errors.pinCode = "Please enter pin code.";
    } else if (!/^\d{6}$/.test(form.pinCode)) {
      errors.pinCode = "Please enter valid pin code.";
    }
    if (isEmpty(form.city)) {
      errors.city = "Please enter city.";
    }
    if (isEmpty(form.state)) {
      errors.state = "Please enter state.";
    }
    if (isEmpty(form.country)) {
      errors.country = "Please enter country.";
    }
    if (isEmpty(form.source)) {
      errors.source = "Please enter source.";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    const payload = {
      leadUID: form.leadUID,
      name: form.name,
      email: form.email,
      gender: form.gender,
      pinCode: form.pinCode,
      city: form.city,
      state: form.state,
      country: form.country,
      source: form.source,
    };
    try {
      const res = await UpdateBasicDetails(payload);
      if (res?.status === "success") {
        setAlert({
          type: "success",
          message: res?.message,
        });
        setErrors({});
        setForm({});
        setTimeout(() => {
          navigate("/my-leads", { replace: true });
        }, 1900);
      } else {
        setAlert({
          type: "error",
          message:
            res?.message || "Failed to update lead details. Please try again.",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error?.message || "Server error. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="max-w-8xl mx-auto bg-white rounded-3xl shadow-lg overflow-visible">
          {/* Header */}
          <div className="relative overflow-hidden border-b border-slate-200 bg-white px-8 py-6">
            <div className="absolute top-0 right-0 h-32 w-32 rounded-full bg-blue-100 blur-3xl opacity-60"></div>

            <div className="relative flex items-center justify-between">
              {/* LEFT */}
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 border border-blue-100 shadow-sm">
                  <UserCheck className="text-blue-600" size={28} />
                </div>

                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-800">
                      Edit Basic Details
                    </h2>

                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
                      Update
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-slate-500">
                    Update candidate basic information
                  </p>
                </div>
              </div>

              {/* RIGHT */}
              <div className="hidden md:flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>

                <span className="text-sm font-medium text-slate-600">
                  Recruitment CRM
                </span>
              </div>
            </div>
          </div>

          {/* BODY */}
          <div className="p-8 space-y-8">
            {/* BASIC DETAILS */}
            <div>
              <div className="grid md:grid-cols-3 gap-5">
                <InputField
                  label="Full Name"
                  required
                  placeHolder="Enter Full Name"
                  icon={User}
                  value={form.name || ""}
                  onChange={handleChange("name")}
                  error={errors.name}
                />

                {/* PHONE */}
                <div>
                  <InputField
                    label="Phone Number"
                    required
                    placeHolder="+91 9876543210"
                    icon={Phone}
                    value={form.phoneNo || ""}
                    readOnly
                  />

                  {form.phoneNo && (
                    <p className="text-xs text-amber-500 mt-1 ml-1 flex gap-2 items-center">
                      <Lock size={12} />
                      Phone number cannot be edited
                    </p>
                  )}
                </div>

                <InputField
                  label="Email Address"
                  placeHolder="Enter Email"
                  icon={Mail}
                  value={form.email || ""}
                  onChange={handleChange("email")}
                  error={errors.email}
                />

                {/* GENDER */}
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Gender <span className="text-red-500">*</span>
                  </label>

                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                    <User className="text-gray-400 mr-2" size={18} />

                    <select
                      value={form.gender || ""}
                      onChange={handleChange("gender")}
                      className="w-full bg-transparent outline-none text-sm"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {errors.gender && (
                    <p className="text-xs text-red-500">{errors.gender}</p>
                  )}
                </div>

                <InputField
                  label="Pin Code"
                  required
                  placeHolder="000000"
                  icon={Hash}
                  value={form.pinCode || ""}
                  onChange={handleChange("pinCode")}
                  error={errors.pinCode}
                  maxLength={6}
                />

                <div className="space-y-1 relative z-50 overflow-visible">
                  <label className="text-sm font-semibold text-gray-700">
                    City <span className="text-red-500">*</span>
                  </label>

                  <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                    <MapPin className="text-gray-400 mr-2" size={18} />

                    <input
                      type="text"
                      value={form.city || ""}
                      placeholder="Enter or select city"
                      className="w-full bg-transparent outline-none text-sm"
                      onFocus={() => {
                        setShowCityDropdown(true);
                        setFilteredCities(cityOptions);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setShowCityDropdown(false);
                        }, 150);
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        setForm((prev) => ({
                          ...prev,
                          city: value,
                        }));

                        const filtered = cityOptions.filter((city) =>
                          city.toLowerCase().includes(value.toLowerCase()),
                        );

                        setFilteredCities(filtered);
                        setShowCityDropdown(true);
                      }}
                    />
                  </div>

                  {showCityDropdown && filteredCities.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-[9999] w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                      {filteredCities.map((city, index) => (
                        <div
                          key={index}
                          onMouseDown={() => {
                            setForm((prev) => ({
                              ...prev,
                              city,
                            }));

                            setShowCityDropdown(false);
                          }}
                          className="px-4 py-2 text-sm cursor-pointer transition hover:bg-gray-100"
                        >
                          {city}
                        </div>
                      ))}
                    </div>
                  )}

                  {errors.city && (
                    <p className="text-xs text-red-500">{errors.city}</p>
                  )}
                </div>

                <InputField
                  label="State"
                  required
                  placeHolder="Enter State"
                  icon={Map}
                  value={form.state || ""}
                  onChange={handleChange("state")}
                  error={errors.state}
                />

                <InputField
                  label="Country"
                  required
                  placeHolder="Enter Country"
                  icon={Globe}
                  value={form.country || ""}
                  onChange={handleChange("country")}
                  error={errors.country}
                />
              </div>
            </div>

            {/* SOURCE */}
            <div className="space-y-1 relative">
              <label className="text-sm font-semibold text-gray-700">
                Source <span className="text-red-500">*</span>
              </label>

              <div className="flex items-center border border-gray-200 rounded-xl px-3 py-2 bg-white shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
                <Link className="text-gray-400 mr-2" size={18} />

                <input
                  type="text"
                  onKeyDown={(e) => {
                    if (!showSourceDropdown) return;

                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setActiveSourceIndex((prev) =>
                        prev < filteredSources.length - 1 ? prev + 1 : 0,
                      );
                    }

                    if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setActiveSourceIndex((prev) =>
                        prev > 0 ? prev - 1 : filteredSources.length - 1,
                      );
                    }

                    if (e.key === "Enter") {
                      e.preventDefault();

                      if (
                        activeSourceIndex >= 0 &&
                        filteredSources[activeSourceIndex]
                      ) {
                        setForm((prev) => ({
                          ...prev,
                          source: filteredSources[activeSourceIndex].source,
                        }));
                      }

                      setShowSourceDropdown(false);
                      setActiveSourceIndex(-1);
                    }

                    if (e.key === "Escape") {
                      setShowSourceDropdown(false);
                      setActiveSourceIndex(-1);
                    }
                  }}
                  value={form.source || ""}
                  placeholder="Type or select source"
                  className="w-full bg-transparent outline-none text-sm"
                  onFocus={() => {
                    setShowSourceDropdown(true);
                    setFilteredSources(sourceOptions);
                    setActiveSourceIndex(-1);
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSourceDropdown(false), 150);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;

                    setForm((prev) => ({
                      ...prev,
                      source: value,
                    }));

                    const filtered = sourceOptions.filter((item) =>
                      item.source.toLowerCase().includes(value.toLowerCase()),
                    );

                    setFilteredSources(filtered);
                    setShowSourceDropdown(true);
                  }}
                />
              </div>

              {/* Dropdown */}
              {showSourceDropdown && filteredSources.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 z-[9999] w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto">
                  {filteredSources.map((item, index) => (
                    <div
                      key={item.id}
                      onMouseDown={() => {
                        setForm((prev) => ({
                          ...prev,
                          source: item.source,
                        }));
                        setShowSourceDropdown(false);
                        setActiveSourceIndex(-1);
                      }}
                      className={`px-4 py-2 text-sm cursor-pointer transition ${
                        index === activeSourceIndex
                          ? "bg-blue-100 text-blue-700"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {item.source}
                    </div>
                  ))}
                </div>
              )}

              {showSourceDropdown &&
                form.source &&
                filteredSources.length === 0 &&
                sourceOptions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-2 z-[9999] w-full bg-white border border-gray-200 rounded-xl shadow-2xl px-4 py-2 text-sm text-gray-500">
                    No matches found — you can use your entered value
                  </div>
                )}

              {errors.source && (
                <p className="text-xs text-red-500">{errors.source}</p>
              )}
            </div>
          </div>

          {/* FOOTER BUTTONS */}
          <div className="flex justify-end gap-4 px-8 pb-6">
            <button
              onClick={() => navigate("/my-leads")}
              className="px-6 py-3 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-3 rounded-xl bg-primary text-white font-semibold shadow-md transition flex items-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="animate-spin" size={18} />}
              Update Details
            </button>
          </div>
        </div>
      </div>

      {alert && (
        <NotificationAlert
          show={!!alert}
          type={alert?.type}
          message={alert?.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default EditBasicDetails;
